// LifeStage.jsx
import { useState } from 'react';
import s from '../shared.module.css';
import l from './lifestage.module.css';
import PatientHeader from '../PatientHeader';
import CarePlanSection from '../CarePlanSection';

const JOURNEY_STAGES = [
  { icon: '👧', label: 'Adolescence', done: true },
  { icon: '🌸', label: 'Young Adult', done: true },
  { icon: '💜', label: 'Reproductive Age', active: true },
  { icon: '🤰', label: 'Pregnancy', muted: true },
  { icon: '👶', label: 'Postpartum', muted: true },
  { icon: '🌺', label: 'Perimenopause', muted: true },
  { icon: '🍂', label: 'Menopause', muted: true },
  { icon: '🌟', label: "Senior Women's Health", muted: true },
];

const TABS = ['Overview', 'Menstrual Health', 'Reproductive Health', 'Fertility', 'Assessments', 'Preventive Care', 'Lifestyle', 'Notes'];

export default function LifeStage() {
  const [activeTab, setActiveTab] = useState('Overview');

  return (
    <div className={s.pageWrap}>
      <div className={s.pageHead}>
        <div className={s.pageHeadLeft}>
          <h1 className={s.pageTitle}>Life Stage Details</h1>
          <p className={s.pageSubtitle}>Dynamic classification, transition signals, and care focus tailored to the patient's current stage.</p>
        </div>
        <div className={s.pageHeadActions}>
          <button className={s.btnOutline}>✏️ Update Stage</button>
          <button className={s.btnPrimary}>+ Add Note</button>
        </div>
      </div>

      <PatientHeader
        summaryFields={[
          { groupLabel: 'Record Summary', label: 'BMI', value: '25.9 (Overweight)' },
          { label: 'Last Visit', value: '12 May 2025' },
          { label: 'Care Team', value: 'Dr. Ananya Sharma & 1 more' },
        ]}
      />

      {/* Confidence stats row */}
      <div className={l.confidenceRow}>
        <div className={l.confCard}>
          <p className={l.confLabel}>Current Life Stage</p>
          <p className={l.confValuePrimary}>Reproductive Age</p>
          <p className={l.confSub} style={{ color: 'var(--ava-color-secondary-500)', fontWeight: 600 }}>Preconception / Fertility Readiness</p>
        </div>
        <div className={l.confCard}>
          <p className={l.confLabel}>Confidence Score</p>
          <p className={l.confValueGreen}>High</p>
          <div className={l.progressBar}>
            <div className={l.progressFill} style={{ width: '92%' }} />
          </div>
          <p className={l.confSub}>92%</p>
        </div>
        <div className={l.confCard}>
          <p className={l.confLabel}>Transition Signals</p>
          {['Ovulatory cycles', 'Stable ± 2–4 days', 'Actively planning pregnancy'].map((t, i) => (
            <p key={i} className={l.confSub} style={{ margin: '2px 0' }}>• {t}</p>
          ))}
          <span className={l.transitionTag}>Ongoing</span>
        </div>
        <div className={l.confCard}>
          <p className={l.confLabel}>Stage Since</p>
          <p className={l.confValuePrimary}>Jan 2023</p>
          <p className={l.confSub}>1 year 4 months</p>
        </div>
        <div className={l.confCard}>
          <p className={l.confLabel}>Next Likely Stage</p>
          <p className={l.confValuePrimary}>Pregnancy</p>
          <p className={l.confSub}>Likelihood: </p>
          <span className={s.badgeOrange}>Moderate</span>
          <p className={l.confSub} style={{ marginTop: 4 }}>In next 6–12 months</p>
        </div>
      </div>

      {/* Journey timeline */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <h2 className={s.sectionTitle} style={{ marginBottom: 14 }}>Women's Life Stage Journey</h2>
        <div className={l.journeyTimeline}>
          {JOURNEY_STAGES.map((st, i) => (
            <div key={i} className={l.journeyItem}>
              {i < JOURNEY_STAGES.length - 1 && (
                <div className={`${l.journeyLine} ${st.done ? l.journeyLineDone : ''}`} />
              )}
              <div className={`${l.journeyDot} ${st.active ? l.journeyDotActive : st.done ? l.journeyDotDone : ''}`}
                style={st.muted ? { border: '2px dashed var(--ava-color-gray-300)', background: 'var(--ava-color-gray-100)' } : {}}
              >
                {st.icon}
              </div>
              <p className={`${l.journeyLabel} ${st.active ? l.journeyLabelActive : st.muted ? l.journeyLabelMuted : ''}`}>
                {st.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Stage content 4 panels */}
      <div className={l.stageContent}>
        <div className={l.stagePanel}>
          <p className={l.stagePanelTitle}><span className={l.stagePanelIcon}>📖</span> Stage Definition / Overview</p>
          <p style={{ fontSize: 11, color: 'var(--ava-color-body)', lineHeight: 1.6, margin: '0 0 14px' }}>
            Reproductive Age encompasses the years when women are physically capable of becoming pregnant. This phase focuses on reproductive health, cycle regularity, fertility optimization, and preparation for a healthy pregnancy.
          </p>
          <p className={l.stagePanelTitle} style={{ fontSize: 12, margin: '0 0 8px' }}>Key Physiological Characteristics</p>
          {['Regular ovulatory menstrual cycles', 'Peak fertility window (ideal conception years)', 'Hormonal balance with estrogen & progesterone cycling', 'Optimal time for family planning and preconception care'].map((b, i) => (
            <div key={i} className={l.bulletItem}><span className={l.bulletDot} />{b}</div>
          ))}
          <p className={l.stagePanelTitle} style={{ fontSize: 12, margin: '14px 0 8px' }}>Typical Care Priorities</p>
          {['Fertility awareness & preconception optimization', 'Cycle health, ovulation tracking & nutrition', 'Mental well-being & stress management', 'Immunizations & genetic risk assessment'].map((b, i) => (
            <div key={i} className={l.bulletItem}><span className={l.bulletDot} style={{ background: 'var(--ava-color-success-500)' }} />✓ {b}</div>
          ))}
        </div>

        <div className={l.stagePanel}>
          <p className={l.stagePanelTitle}><span className={l.stagePanelIcon}>📊</span> Stage-specific Symptoms & Indicators</p>
          {['Mild cyclic cramping', 'Breast tenderness (luteal phase)', 'Ovulation signs (cervical mucus, mild mid-cycle pain)', 'Energy & mood variations across cycle'].map((b, i) => (
            <div key={i} className={l.bulletItem}><span className={l.bulletDot} style={{ background: 'var(--ava-color-success-500)' }} />{b}</div>
          ))}

          <p className={l.stagePanelTitle} style={{ fontSize: 12, margin: '14px 0 8px' }}><span>⚠️</span> Risk Flags & Watchouts</p>
          {['Irregular cycles (>35 days or <21 days)', 'History of PCOS or insulin resistance', 'Thyroid dysfunction', 'Vitamin D deficiency', 'BMI outside healthy range'].map((b, i) => (
            <div key={i} className={l.warnItem}><span className={l.warnIcon}>▲</span>{b}</div>
          ))}

          <p className={l.stagePanelTitle} style={{ fontSize: 12, margin: '14px 0 8px' }}>Recommended Screenings & Assessments</p>
          {['CBC, TSH, HbA1c', 'Vitamin D, B12, Ferritin', 'Thyroid profile', 'Rubella, Varicella immunity', 'Pelvic ultrasound (if indicated)'].map((b, i) => (
            <div key={i} className={l.bulletItem}><span className={l.bulletDot} style={{ background: 'var(--ava-color-primary-400)' }} />{b}</div>
          ))}

          <p className={l.stagePanelTitle} style={{ fontSize: 12, margin: '14px 0 8px' }}>AI-Generated Next Best Actions</p>
          {['Complete fertility readiness assessment', 'Optimize nutrition, folic acid & lifestyle', 'Screen for thyroid & metabolic risks', 'Track ovulation & cycle regularity'].map((b, i) => (
            <div key={i} className={l.bulletItem}><span className={l.bulletDot} style={{ background: 'var(--ava-color-secondary-500)' }} />{b}</div>
          ))}
        </div>
      </div>

      {/* Relevant history */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <p className={s.sectionTitle} style={{ marginBottom: 12 }}>Relevant History and Context</p>
        <div className={l.histTable}>
          {[
            { k: 'Menarche Age', v: '13 years' }, { k: 'Goush...', v: 'Yes' },
            { k: 'Cycle Pattern', v: 'Regular (28–30 days)' }, { k: 'Contraception', v: 'None (Actively trying)' },
            { k: 'Cycle Length', v: '29 ± 2 days' }, { k: 'Gravida / Para', v: 'G0 P0' },
            { k: 'Sexual Activity', v: '' }, { k: '', v: '' },
          ].map((r, i) => r.k && (
            <div key={i} className={l.histRow}>
              <span className={l.histKey}>{r.k}</span>
              <span className={l.histVal}>{r.v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Stage Specific Data tabs */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}>Stage-Specific Data</h2>
        </div>
        <div className={s.tabBar}>
          {TABS.map(t => (
            <button key={t} className={`${s.tab} ${activeTab === t ? s.tabActive : ''}`} onClick={() => setActiveTab(t)}>{t}</button>
          ))}
        </div>
        {activeTab === 'Overview' && (
          <div className={l.stageDataGrid}>
            {[
              { title: '📋 Menstrual Profile', icon: '📋', value: 'Regular', sub: '29 ± 2 days\nDuration: 4–5 days', link: 'View Details', valueClass: '' },
              { title: '💜 Fertility Intent', icon: '💜', value: 'Actively Trying', sub: 'Trying since: Jan 2025\nGoal: Pregnancy in 6 months', link: 'Update', valueClass: 'stageDataValueGreen' },
              { title: '🛡️ Contraception History', icon: '🛡️', value: 'None', sub: 'Previously: OCP (3 yrs ago)\nCurrent: Not using', link: 'View History', valueClass: '' },
              { title: '🤰 Obstetric History', icon: '🤰', value: 'G0 P0', sub: 'No prior pregnancies\nNo pregnancy loss', link: 'Add History', valueClass: '' },
              { title: '⚠️ Active Concerns', icon: '⚠️', value: 'None Reported', sub: 'Feels well overall', link: 'Add Concern', valueClass: '' },
              { title: '🔍 Preventive Gaps', icon: '🔍', value: 'TSH Screening', sub: 'Vitamin D Test\nRubella Immunity', link: 'Address Now', valueClass: 'stageDataValueOrange' },
            ].map((d, i) => (
              <div key={i} className={l.stageDataCard}>
                <p className={l.stageDataTitle}><span className={l.stageDataTitleIcon}>{d.icon}</span>{d.title.replace(d.icon, '')}</p>
                <p className={`${l.stageDataValue} ${l[d.valueClass] || ''}`}>{d.value}</p>
                {d.sub.split('\n').map((sub, j) => <p key={j} className={l.stageDataSub}>{sub}</p>)}
                <button className={l.stageDataLink}>{d.link}</button>
              </div>
            ))}
          </div>
        )}
        {activeTab !== 'Overview' && (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--ava-color-caption)', fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <p>{activeTab} content available in this tab.</p>
          </div>
        )}
      </div>

      {/* AI Life Stage Insights */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}>AI Life Stage Insights <span style={{ color: 'var(--ava-color-secondary-500)' }}>✨</span></h2>
        </div>
        <div className={l.insightGrid}>
          {[
            { icon: '📋', title: 'Complete fertility readiness assessment', desc: 'Evaluate key biomarkers and optimize factors for conception.', btn: 'Start Assessment >' },
            { icon: '🧪', title: 'Order TSH and HbA1c screening', desc: 'Early detection helps improve fertility outcomes.', btn: 'Order Tests >' },
            { icon: '🥗', title: 'Start preconception nutrition plan', desc: 'Personalized nutrition to support fertility & pregnancy health.', btn: 'View Plan >' },
            { icon: '📅', title: 'Book gynecology consultation', desc: 'Discuss cycle tracker data and preconception plan.', btn: 'Book An Appointment >' },
          ].map((ins, i) => (
            <div key={i} className={l.insightCard}>
              <div className={l.insightIcon}>{ins.icon}</div>
              <p className={l.insightTitle}>{ins.title}</p>
              <p className={l.insightDesc}>{ins.desc}</p>
              <button className={l.insightBtn}>{ins.btn}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Care Plan */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}>Care Plan & Milestones</h2>
        </div>
        <CarePlanSection
          followup={{ date: '01 Jun 2025, 10:30 AM', doctor: 'Dr. Ananya Sharma', specialty: 'Gynecologist', clinic: "Avexa Women's Health Clinic, Mumbai" }}
        />
      </div>
    </div>
  );
}